import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Download, DollarSign, TrendingUp, CreditCard, ArrowDownCircle } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import kitchenApi from '../services/kitchenApiService';

const PERIOD_OPTIONS = [
  { value: 'week',  label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year',  label: 'This Year' },
];

const fmt$ = (n) => `$${(n || 0).toFixed(2)}`;

export default function FinancePage() {
  const [period,  setPeriod]  = useState('month');
  const [loading, setLoading] = useState(true);
  const [data,    setData]    = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [balance, setBalance] = useState(null);

  const loadData = useCallback(async (p = period) => {
    setLoading(true);
    const [finR, payR, balR] = await Promise.all([
      kitchenApi.getFinancialReports(p, 'revenue'),
      kitchenApi.getPayoutTransactions(p),
      kitchenApi.getPayoutBalance(),
    ]);
    if (finR.success)  setData(finR.data);
    if (payR.success)  setPayouts(payR.data);
    if (balR.success)  setBalance(balR.data);
    setLoading(false);
  }, [period]);

  useEffect(() => { loadData(period); }, [period]);

  const d  = data   || {};
  const s  = d.summary || {};
  const b  = balance || {};
  const tx = payouts?.transactions || payouts || [];
  const chartData = d.daily_breakdown || d.revenue_chart || [];

  const handleExport = async () => {
    const res = await kitchenApi.exportFinancialReport(period, 'revenue', 'csv');
    if (res.success && res.data?.url) window.open(res.data.url, '_blank');
  };

  return (
    <div>
      <div className="k-page-header">
        <div>
          <h1 className="k-page-title">Finance</h1>
          <p className="k-page-sub">Revenue, earnings and transaction history</p>
        </div>
        <div className="k-page-actions">
          <select className="k-select" style={{ width: 160 }} value={period} onChange={e => setPeriod(e.target.value)}>
            {PERIOD_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button className="k-btn k-btn-secondary" onClick={handleExport}><Download size={15} /> Export</button>
          <button className="k-btn-icon" onClick={() => loadData(period)} title="Refresh"><RefreshCw size={16} /></button>
        </div>
      </div>

      {/* Balance card */}
      {balance && (
        <div style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #E85A26 100%)', borderRadius: 16, padding: 24, marginBottom: 24, color: '#fff', position: 'relative', overflow: 'hidden' }}>
          <div style={{ fontSize: 13, fontWeight: 600, opacity: .8, marginBottom: 8 }}>Available Balance</div>
          <div style={{ fontSize: 36, fontWeight: 800, marginBottom: 4 }}>{fmt$(b.available_balance ?? b.balance)}</div>
          <div style={{ fontSize: 13, opacity: .8 }}>
            Pending: {fmt$(b.pending_balance ?? b.pending)} · 
            Total Earned: {fmt$(b.total_earned ?? b.lifetime_earnings)}
          </div>
          <div style={{ position: 'absolute', right: -20, top: -20, width: 120, height: 120, background: 'rgba(255,255,255,.08)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', right: 20, bottom: -30, width: 80, height: 80, background: 'rgba(255,255,255,.06)', borderRadius: '50%' }} />
        </div>
      )}

      {/* KPI Cards */}
      <div className="k-kpi-grid" style={{ marginBottom: 24 }}>
        {loading ? (
          Array(4).fill(0).map((_, i) => <div key={i} className="k-card"><div className="k-skeleton" style={{ height: 80 }} /></div>)
        ) : (
          <>
            <div className="k-kpi-card">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div><div className="k-kpi-label">Total Revenue</div><div className="k-kpi-value" style={{ fontSize: 22, marginTop: 6 }}>{fmt$(s.total_revenue)}</div></div>
                <div className="k-kpi-icon-wrap" style={{ background: '#FFF7F3' }}><DollarSign size={20} color="#FF6B35" /></div>
              </div>
              {s.revenue_trend !== undefined && <div className={`k-kpi-trend ${s.revenue_trend >= 0 ? 'up' : 'down'}`}>↑ {Math.abs(s.revenue_trend)}%</div>}
            </div>
            <div className="k-kpi-card">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div><div className="k-kpi-label">Net Profit</div><div className="k-kpi-value" style={{ fontSize: 22, marginTop: 6 }}>{fmt$(s.net_profit)}</div></div>
                <div className="k-kpi-icon-wrap" style={{ background: '#ECFDF5' }}><TrendingUp size={20} color="#10B981" /></div>
              </div>
            </div>
            <div className="k-kpi-card">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div><div className="k-kpi-label">Total Expenses</div><div className="k-kpi-value" style={{ fontSize: 22, marginTop: 6 }}>{fmt$(s.total_expenses)}</div></div>
                <div className="k-kpi-icon-wrap" style={{ background: '#FEF2F2' }}><CreditCard size={20} color="#EF4444" /></div>
              </div>
            </div>
            <div className="k-kpi-card">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div><div className="k-kpi-label">Profit Margin</div><div className="k-kpi-value" style={{ fontSize: 22, marginTop: 6 }}>{(s.profit_margin || 0).toFixed(1)}%</div></div>
                <div className="k-kpi-icon-wrap" style={{ background: '#F5F3FF' }}><ArrowDownCircle size={20} color="#8B5CF6" /></div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Charts */}
      <div className="k-grid-2" style={{ marginBottom: 20 }}>
        <div className="k-card">
          <div className="k-card-title" style={{ marginBottom: 16 }}>Revenue Trend</div>
          {loading ? (
            <div className="k-skeleton" style={{ height: 180 }} />
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="fg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#FF6B35" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip formatter={v => [`$${(v||0).toFixed(2)}`, 'Revenue']} />
                <Area type="monotone" dataKey="revenue" stroke="#FF6B35" strokeWidth={2.5} fill="url(#fg)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div className="k-empty" style={{ padding: '30px 0' }}><div className="k-empty-icon">📈</div><div className="k-empty-title">No data for this period</div></div>}
        </div>

        <div className="k-card">
          <div className="k-card-title" style={{ marginBottom: 16 }}>Profit vs Expenses</div>
          {loading ? (
            <div className="k-skeleton" style={{ height: 180 }} />
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip />
                <Bar dataKey="profit" fill="#10B981" name="Profit" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="k-empty" style={{ padding: '30px 0' }}><div className="k-empty-icon">💰</div><div className="k-empty-title">No data for this period</div></div>}
        </div>
      </div>

      {/* Revenue breakdown */}
      {(d.revenue_breakdown || []).length > 0 && (
        <div className="k-card" style={{ marginBottom: 20 }}>
          <div className="k-card-title" style={{ marginBottom: 16 }}>Revenue Breakdown</div>
          <div className="k-table-wrap">
            <table className="k-table">
              <thead><tr><th>Category</th><th>Orders</th><th>Amount</th><th>% Share</th></tr></thead>
              <tbody>
                {(d.revenue_breakdown || []).map((row, i) => (
                  <tr key={i}>
                    <td style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: row.color || '#FF6B35', flexShrink: 0 }} />
                      {row.category}
                    </td>
                    <td>{row.orders}</td>
                    <td style={{ fontWeight: 700 }}>{fmt$(row.amount)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: '#F1F5F9', borderRadius: 999 }}>
                          <div style={{ height: '100%', width: `${row.percentage || 0}%`, background: row.color || '#FF6B35', borderRadius: 999 }} />
                        </div>
                        <span style={{ fontSize: 12, minWidth: 32 }}>{(row.percentage || 0).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transactions */}
      <div className="k-card">
        <div className="k-card-title" style={{ marginBottom: 16 }}>Recent Transactions</div>
        {loading ? (
          <div className="k-skeleton" style={{ height: 120 }} />
        ) : (Array.isArray(tx) && tx.length > 0) ? (
          <div className="k-table-wrap">
            <table className="k-table">
              <thead><tr><th>Date</th><th>Description</th><th>Type</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>
                {tx.slice(0, 20).map((t, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 13, color: 'var(--k-text-sub)' }}>{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
                    <td>{t.description || t.type || '—'}</td>
                    <td><span className={`k-badge ${t.type === 'payout' ? 'k-badge-accepted' : 'k-badge-active'}`}>{t.type || 'transaction'}</span></td>
                    <td style={{ fontWeight: 700, color: (t.amount || 0) >= 0 ? 'var(--k-success)' : 'var(--k-danger)' }}>
                      {t.amount >= 0 ? '+' : ''}{fmt$(t.amount)}
                    </td>
                    <td><span className={`k-badge k-badge-${(t.status || 'completed').toLowerCase()}`}>{t.status || 'completed'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="k-empty">
            <div className="k-empty-icon">💳</div>
            <div className="k-empty-title">No transactions yet</div>
            <div className="k-empty-sub">Transactions will appear here once orders are processed</div>
          </div>
        )}
      </div>

      {/* Tax info */}
      {d.tax_info && (
        <div className="k-card" style={{ marginTop: 20 }}>
          <div className="k-card-title" style={{ marginBottom: 12 }}>Tax Summary</div>
          {Object.entries(d.tax_info).map(([k, v]) => (
            <div key={k} className="k-flex-between" style={{ padding: '8px 0', borderBottom: '1px solid var(--k-border-light)', fontSize: 14 }}>
              <span style={{ color: 'var(--k-text-sub)', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</span>
              <span style={{ fontWeight: 600 }}>{typeof v === 'number' && k.includes('rate') ? `${v}%` : typeof v === 'number' ? fmt$(v) : v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
